
import { defineComponent } from "vue";
import { ReceiptBaseResponse } from "@/core/interfaces/ApiResponses";
import PhieuXuatKhoService from "@/core/services/Receipt.service";
import ToolService from "@/core/services/Tool.service";
import {
  calcLineAmount,
  formatAmount,
  formatQuantity,
  formatUnitPrice,
} from "@/core/helpers/number-format";
import {
  swalErrNotification,
  transformFirstLetterToUpperCase,
} from "@/core/helpers/utils";
import { useDateTime } from "@/core/hooks/use-date-time-hook";
import PdfViewerPage from "@/components/shared/pdf-viewer/PdfViewerPage.vue";
import PdfDocumentHeader from "@/components/shared/pdf-viewer/PdfDocumentHeader.vue";
import PdfAccountingHeader from "@/components/shared/pdf-viewer/PdfAccountingHeader.vue";
import { generateHtmlElementPdf } from "@/components/shared/pdf-viewer/generate-html-element-pdf";
import { formatTaiKhoanMaCt, pickDonViPdfExtra } from "@/components/shared/pdf-viewer/pdf-document-header";
import {
  getPdfContentWidthPx,
  getPdfPrintContentStyle,
} from "@/components/shared/pdf-viewer/pdf-content.config";

export default defineComponent({
  name: "PhieuXuatKhoViewer",

  components: { PdfViewerPage, PdfDocumentHeader, PdfAccountingHeader },

  setup() {
    const { formatDisplayingDate } = useDateTime();
    return { formatDisplayingDate };
  },

  data() {
    return {
      phieuXuatData: null as ReceiptBaseResponse | null,
      pdfSource: null as string | null,
      isGenerating: false,
      tongTienInText: "",
      tenDonVi: "",
      tenDonViCha: "",
    };
  },

  computed: {
    taiKhoanNo(): string {
      const tkNo = this.phieuXuatData?.tk_no;
      return formatTaiKhoanMaCt(tkNo?.ma_tk, tkNo?.ma_ct);
    },

    taiKhoanCo(): string {
      const tkCo = this.phieuXuatData?.tk_co;
      return formatTaiKhoanMaCt(tkCo?.ma_tk, tkCo?.ma_ct);
    },

    ngayVaoXuongDisplay(): string {
      const ngay = this.phieuXuatData?.bien_ban_giai_the?.ngay_vao_xuong;
      if (!ngay) {
        return ".............................";
      }
      const formatted = this.formatDisplayingDate(ngay, "/");
      return formatted === "-" ? "............................." : formatted;
    },

    pdfFileName(): string {
      if (!this.phieuXuatData) return "phieu-xuat-kho.pdf";
      return `phieu-xuat-kho-${this.phieuXuatData.so_phieu}.pdf`;
    },

    ngayXuatKhoDisplay(): string {
      const dateSource =
        this.phieuXuatData?.ngay ?? this.phieuXuatData?.created_at;
      if (!dateSource) return "Ngày ...... tháng ...... năm ......";
      const date = new Date(dateSource);
      return `Ngày ${date.getDate()} tháng ${
        date.getMonth() + 1
      } năm ${date.getFullYear()}`;
    },

    totalPrice(): number {
      if (!this.phieuXuatData) return 0;
      return this.phieuXuatData.vat_tu_xuat_kho.reduce(
        (sum, { pivot: { don_gia, so_luong } }) =>
          sum + +so_luong * +(don_gia ?? 0),
        0
      );
    },

    signatureLabels(): string[] {
      return [
        "Người nhận hàng",
        "P.Kế hoạch vật tư",
        "Thủ kho",
        "P.Tài chính",
        "Giám đốc",
      ];
    },

    printPaperStyle(): Record<string, string> {
      return getPdfPrintContentStyle();
    },

    hiddenTemplateStyle(): Record<string, string> {
      return { width: `${getPdfContentWidthPx()}px` };
    },
  },

  methods: {
    formatQuantity,
    formatUnitPrice,
    formatAmount,
    calcLineAmount,

    async updateTongTienInText() {
      if (!this.phieuXuatData) {
        this.tongTienInText = "";
        return;
      }

      const total = Math.round(this.totalPrice);
      const {
        data: { data },
      } = await ToolService.convertPriceToText(total);

      this.tongTienInText = transformFirstLetterToUpperCase(data);
    },

    async loadAndGenerate() {
      const id = Number(this.$route.params.id);
      if (!id) return;

      try {
        this.isGenerating = true;
        const { data: response } = await PhieuXuatKhoService.get(id);
        this.phieuXuatData = response.data;
        const { tenDonVi, tenDonViCha } = pickDonViPdfExtra(response.extra);
        this.tenDonVi = tenDonVi;
        this.tenDonViCha = tenDonViCha;

        await this.updateTongTienInText();

        await this.$nextTick();
        await this.$nextTick();

        await this.generatePdf();
      } catch (e) {
        swalErrNotification(e, "Có lỗi khi tải phiếu xuất kho");
      } finally {
        this.isGenerating = false;
      }
    },

    async generatePdf() {
      const el = (this.$refs.printArea as HTMLElement)?.querySelector(
        ".printing-paper"
      ) as HTMLElement | null;
      if (!el) return;

      this.pdfSource = await generateHtmlElementPdf(el);
    },
  },

  mounted() {
    this.loadAndGenerate();
  },
});
